/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { UserInterface } from '../../../models/interface/user.interface';
import { InterventionInstanceInterface } from '../../../models/interface/intervention-instances/intervention-instance.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { filter, take } from 'rxjs/operators';
import { faHands } from '@fortawesome/free-solid-svg-icons/faHands';
import { faHandsHelping } from '@fortawesome/free-solid-svg-icons/faHandsHelping';
import { DiaryInstanceInterface } from '../../../models/interface/diary-instance.interface';
import { DiaryInterface } from '../../../models/interface/diary.interface';
import { BuddyInstanceInterface } from '../../../models/interface/buddy/buddy-instance.interface';
import { BuddyStore } from '../../../store/buddy/component-store/buddy.store';
import { HttpResponse } from '@angular/common/http';
import { HelperDialogService } from 'src/app/services/helper/helper-dialog/helper-dialog.service';

export type DialogBuddyInstanceUpdateData = {
  buddyInstance: BuddyInstanceInterface;
  members: UserInterface[];
  interventions: InterventionInterface[];
  interventionInstances: InterventionInstanceInterface[];
  diaryInstances: DiaryInstanceInterface[];
  diaries: DiaryInterface[];
  studyCollaborators: UserInterface[];
  studyId: number;
  isManager: boolean;
};

@Component({
  selector: 'app-dialog-buddy-instance-update',
  templateUrl: './dialog-buddy-instance-update.component.html',
  styleUrls: ['./dialog-buddy-instance-update.component.css'],
  providers: [BuddyStore],
})
export class DialogBuddyInstanceUpdateComponent implements OnDestroy, OnInit {
  // Icons
  faHands = faHands;
  faHandsHelping = faHandsHelping;

  public members: UserInterface[];
  public membersSelection1Subject = new BehaviorSubject<UserInterface[]>([]);
  public membersSelection2Subject = new BehaviorSubject<UserInterface[]>([]);
  public interventionInstancesBuddy1: InterventionInstanceInterface[] = [];
  public interventionInstancesBuddy2: InterventionInstanceInterface[] = [];
  public diaryInstancesBuddy1: DiaryInstanceInterface[] = [];
  public diaryInstancesBuddy2: DiaryInstanceInterface[] = [];
  public interventions: InterventionInterface[] = [];
  public buddyInstance: BuddyInstanceInterface;
  public diaryInstances: DiaryInstanceInterface[];
  public interventionInstances: InterventionInstanceInterface[];

  public diaries: DiaryInterface[];

  private isManager: boolean = false;

  // Selected buddies
  public selectedBuddy1: UserInterface;
  public selectedDiaryInstance1: DiaryInstanceInterface;

  public selectedBuddy2: UserInterface;
  public selectedDiaryInstance2: DiaryInstanceInterface;

  // Form submission
  submitted = false;

  public updateBuddyResponse$: Observable<any>;
  public updateBuddyResponse = new BehaviorSubject<'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE'>('DEFAULT');

  // Filter
  public filter = { userSelection: '' };

  public studyId: number;

  // Subscription Handler
  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogBuddyInstanceUpdateData,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private dialogRef: MatDialogRef<DialogBuddyInstanceUpdateComponent>,
    private buddyStore: BuddyStore,
  ) {
    this.updateBuddyResponse$ = this.buddyStore.updateBuddyResponse$;
  }

  // Helper function
  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    this.members = this.data.members;
    this.buddyInstance = this.data.buddyInstance;
    this.interventionInstances = this.data.interventionInstances;
    this.diaryInstances = this.data.diaryInstances;
    this.diaries = this.data.diaries;
    this.interventions = this.data.interventions;
    this.studyId = this.data.studyId;
    this.isManager = this.data.isManager;

    if (this.buddyInstance) {
      this.selectedBuddy1 = this.helper.findArrObjById(this.buddyInstance.attributes.buddies_ids[0], this.members);
      this.selectedBuddy2 = this.helper.findArrObjById(this.buddyInstance.attributes.buddies_ids[1], this.members);
      this.membersSelection1Subject.next(
        this.members.filter((user) => user.id.toString() !== this.selectedBuddy2?.id.toString()),
      );
      this.membersSelection2Subject.next(
        this.members.filter((user) => user.id.toString() !== this.selectedBuddy1?.id.toString()),
      );

      this.buddyInstance.attributes.diary_instances_ids.forEach((value: { user_id: number; diary_instance_id: number }) => {
        const diaryInstance: DiaryInstanceInterface = this.helper.findArrObjById(value.diary_instance_id, this.diaryInstances);
        if (diaryInstance) {
          if (diaryInstance.attributes.patient_id.toString() === this.selectedBuddy1?.id.toString()) {
            this.selectedDiaryInstance1 = diaryInstance;
          } else {
            this.selectedDiaryInstance2 = diaryInstance;
          }
        }
      });

      this.resetSelection1();
      this.resetSelection2();
    }
  }

  public onChangeSelection1(): void {
    this.membersSelection1Subject.next(
      this.members.filter((user) => user.id.toString() !== this.selectedBuddy2?.id.toString()),
    );
    this.membersSelection2Subject.next(
      this.members.filter((user) => user.id.toString() !== this.selectedBuddy1?.id.toString()),
    );
    this.resetSelection1();
    this.selectedDiaryInstance1 = null;
  }

  public onChangeSelection2(): void {
    this.membersSelection1Subject.next(
      this.members.filter((user) => user.id.toString() !== this.selectedBuddy2?.id.toString()),
    );
    this.membersSelection2Subject.next(
      this.members.filter((user) => user.id.toString() !== this.selectedBuddy1?.id.toString()),
    );
    this.resetSelection2();
    this.selectedDiaryInstance2 = null;
  }

  public isAllSelected(): boolean {
    return (
      !!this.selectedBuddy1 &&
      !!this.selectedBuddy2 &&
      !!this.selectedDiaryInstance1 &&
      !!this.selectedDiaryInstance2
    );
  }

  getInterventionInstanceOfDiaryInstance(diaryInstance: DiaryInstanceInterface) {
    if (!diaryInstance) {
      return null;
    }
    return this.interventionInstances.find(
      (instance) => instance.id == diaryInstance.attributes.intervention_instance_id,
    );
  }

  public updateBuddyConnection(): void {
    this.submitted = true;
    if (this.isAllSelected() && this.updateBuddyResponse.value === 'DEFAULT') {
      this.updateBuddyResponse.next('LOADING');
      const interventionInstance1 = this.interventionInstances.find(
        (instance) => instance.id == this.selectedDiaryInstance1.attributes.intervention_instance_id,
      );
      const interventionInstance2 = this.interventionInstances.find(
        (instance) => instance.id == this.selectedDiaryInstance2.attributes.intervention_instance_id,
      );
      const payload = {
        buddyId1: this.selectedBuddy1.id,
        interventionInstanceId1: interventionInstance1.id,
        diaryInstanceId1: this.selectedDiaryInstance1.id,
        buddyId2: this.selectedBuddy2.id,
        interventionInstanceId2: interventionInstance2.id,
        diaryInstanceId2: this.selectedDiaryInstance2.id,
        buddyConnectionId: this.buddyInstance.id,
      };
      if (this.isManager) {
        this.buddyStore.updateBuddiesEM(payload);
      } else {
        this.buddyStore.updateBuddies(payload);
      }
      this.subscriptions.push(
        this.updateBuddyResponse$
          .pipe(
            filter((value) => !!value),
            take(1),
          )
          .subscribe((result) => {
            if (result instanceof HttpResponse) {
              this.updateBuddyResponse.next('SUCCESS');
              setTimeout(() => {
                this.submitted = false;
                this.dialogRef.close(result);
              }, 2500);
            } else {
              this.updateBuddyResponse.next('FAILURE');
              setTimeout(() => {
                this.submitted = false;
                this.updateBuddyResponse.next('DEFAULT');
              }, 2500);
            }
          })
      );
    } else {
      this.submitted = false;
    }
  }

  public openDialogInterventionInstanceDetails(interventionInstanceId: number): void {
    this.helperDialog
      .openDialogInterventionInstanceDetails(interventionInstanceId)
      .afterClosed()
      .subscribe(() => {});
  }

  public openDialogDiaryPreview(diaryInstance: DiaryInstanceInterface): void {
    const diary: DiaryInterface = this.helper.findArrObjById(diaryInstance.attributes.diary_id, this.diaries);
    this.helperDialog
      .openDialogDiaryPreview(diary)
      .afterClosed()
      .subscribe(() => {});
  }

  public openDialogAnswersheetPreview(diaryInstance: DiaryInstanceInterface): void {
    const diary = this.helper.findArrObjById(diaryInstance.attributes.diary_id, this.diaries);
    this.helperDialog
      .openDialogDiaryAnswersheetPreview(diaryInstance.id, diary, this.isManager)
      .afterClosed()
      .subscribe(() => {});
  }

  public resetFilter(): void {
    this.filter.userSelection = '';
    if (this.selectedBuddy2) {
      this.membersSelection1Subject.next(
        this.members.filter((user) => user.id.toString() !== this.selectedBuddy2?.id.toString()),
      );
    }
    if (this.selectedBuddy1) {
      this.membersSelection2Subject.next(
        this.members.filter((user) => user.id.toString() !== this.selectedBuddy1?.id.toString()),
      );
    }
  }

  private resetSelection1(): void {
    this.interventionInstancesBuddy1 = this.interventionInstances.filter(
      (instance) => instance.attributes.patient_id.toString() === this.selectedBuddy1?.id.toString(),
    );
    this.diaryInstancesBuddy1 = this.diaryInstances.filter(
      (instance) => instance.attributes.patient_id.toString() === this.selectedBuddy1?.id.toString(),
    );
  }

  private resetSelection2(): void {
    this.interventionInstancesBuddy2 = this.interventionInstances.filter(
      (instance) => instance.attributes.patient_id.toString() === this.selectedBuddy2?.id.toString(),
    );
    this.diaryInstancesBuddy2 = this.diaryInstances.filter(
      (instance) => instance.attributes.patient_id.toString() === this.selectedBuddy2?.id.toString(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
